import { useEffect, useState } from 'react';
import { Download } from '@mui/icons-material';
import { IconButton, Tooltip, Chip } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { DocumentsAPI } from '@/services/documentsAPI';
import { WorkerSignedDocument } from '@/types/documents';

type Props = {
  workerId: number;
};

const SignedDocumentsCard = ({ workerId }: Props) => {
  const [documents, setDocuments] = useState<WorkerSignedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSignedDocuments = async () => {
      try {
        setLoading(true);
        const response = await DocumentsAPI.getWorkerSignedDocuments(workerId);
        if (response.success) {
          setDocuments(response.data);
        }
      } catch (err) {
        setError('Failed to load signed documents');
        console.error('Error fetching signed documents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedDocuments();
  }, [workerId]);

  const handleDownload = async (doc: WorkerSignedDocument) => {
    if (!doc.pdf_ready) return;
    setDownloading(doc.id);
    setError(null);
    try {
      const response = await DocumentsAPI.getWorkerSignedDocumentDownloadUrl(
        workerId,
        doc.id,
      );
      if (response.success && response.download_url) {
        // The URL is cross-origin S3, so an anchor's `download` attribute is
        // ignored — open it instead and let the response headers drive it.
        window.open(response.download_url, '_blank');
      } else {
        setError(response.error || 'Failed to download document');
      }
    } catch (err) {
      setError('Failed to download document');
      console.error('Error downloading signed document:', err);
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Card title="Signed Documents">
        <Card.Section>
          <Body>Loading...</Body>
        </Card.Section>
      </Card>
    );
  }

  // A download failure must not blank out the list, so only take over the card
  // when there is nothing to show anyway.
  if (error && documents.length === 0) {
    return (
      <Card title="Signed Documents">
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      </Card>
    );
  }

  if (documents.length === 0) {
    return (
      <Card title="Signed Documents">
        <Card.Section>
          <Body color="lighter">No signed documents yet</Body>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title="Signed Documents">
      <Card.Section>
        {error && (
          <div style={{ marginBottom: '12px' }}>
            <Body color="danger">{error}</Body>
          </div>
        )}
        <Stack vertical gap={12}>
          {documents.map((doc) => (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                borderLeft: '4px solid #10B981',
              }}
            >
              <Stack vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Body weight="semibold">
                  {doc.document_name || 'Unknown Document'}
                </Body>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <Small color="lighter">
                    Signed {formatDate(doc.signed_at)}
                  </Small>
                  {doc.version_number && (
                    <Small color="lighter">v{doc.version_number}</Small>
                  )}
                </div>
              </Stack>
              <div
                style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
              >
                {!doc.pdf_ready && (
                  <Chip label="Processing" size="small" color="warning" />
                )}
                <Tooltip
                  title={
                    doc.pdf_ready
                      ? 'Download signed PDF'
                      : 'PDF is still being generated'
                  }
                >
                  <span>
                    <IconButton
                      disabled={!doc.pdf_ready || downloading === doc.id}
                      size="small"
                      onClick={() => handleDownload(doc)}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default SignedDocumentsCard;
